import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AutocompleteDropdown,
  Box,
  Controller,
  ControllerWrapper,
  DateInput,
  FieldWrapper,
  formatSnakeCaseToGeneral,
  FormFieldsContainer,
  Label,
  MaskTextInput,
  RegularField,
  useFormContext,
  useResponsive,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import masks from '@/common/masks';
import { IAuthorizedUserDtoRelationshipTypeEnum } from '@/dtos';
import { AddFamilyMemberForm, AddFamilyMemberFormField } from "@/modules/UseMyMoney/widgets/FieldSetup/hooks/useAddFamilyMemberForm";

const RELATIONSHIP_OPTIONS = Object.values(IAuthorizedUserDtoRelationshipTypeEnum).map((value) => ({
  key: value,
  value,
  title: capitalize(formatSnakeCaseToGeneral(value)),
}));

const AddFamilyMemberModalForm = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const {
    control,
    formState: { errors },
  } = useFormContext<AddFamilyMemberForm>();
  
  return (
    <Box
      pad={isMobile ? 'spacing16' : { vertical: 'spacing24', horizontal: 'spacing32' }}
      background="canvas"
      round="container1Round"
    >
      <FormFieldsContainer>
        <FieldWrapper>
          <Label
            name={AddFamilyMemberFormField.firstName}
            isNarrow
            showRequireIcon
          >
            {t('First name')}
          </Label>
          <ControllerWrapper errorText={errors?.[AddFamilyMemberFormField.firstName]?.message}>
            <Controller
              name={AddFamilyMemberFormField.firstName}
              control={control}
              render={({ field }) => (
                <RegularField
                  testId="add-family-member-from_first-name"
                  name={AddFamilyMemberFormField.firstName}
                  value={field.value}
                  onChange={field.onChange}
                  editMode
                  placeholder={t('Enter first name')}
                  isError={!!errors?.[AddFamilyMemberFormField.firstName]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label
            name={AddFamilyMemberFormField.lastName}
            isNarrow
            showRequireIcon
          >
            {t('Last name')}
          </Label>
          <ControllerWrapper errorText={errors?.[AddFamilyMemberFormField.lastName]?.message}>
            <Controller
              name={AddFamilyMemberFormField.lastName}
              control={control}
              render={({ field }) => (
                <RegularField
                  testId="add-family-member-from_last-name"
                  name={AddFamilyMemberFormField.lastName}
                  value={field.value}
                  onChange={field.onChange}
                  editMode
                  placeholder={t('Enter last name')}
                  isError={!!errors?.[AddFamilyMemberFormField.lastName]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label
            name={AddFamilyMemberFormField.dateOfBirth}
            isNarrow
            showRequireIcon
          >
            {t('Date of Birth')}
          </Label>
          <ControllerWrapper errorText={errors?.[AddFamilyMemberFormField.dateOfBirth]?.message}>
            <Controller
              name={AddFamilyMemberFormField.dateOfBirth}
              control={control}
              render={({ field }) => (
                <DateInput
                  testId="add-family-member-from_date-of-birth"
                  name={AddFamilyMemberFormField.dateOfBirth}
                  maxDate={dayjs()}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="MM/DD/YYYY"
                  isError={!!errors?.[AddFamilyMemberFormField.dateOfBirth]?.message}
                  isManualDateInput
                  selectDateWithoutApply
                  isPicker
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label name={AddFamilyMemberFormField.ssn} isNarrow>
            {t('SSN')}
          </Label>
          <ControllerWrapper errorText={errors?.[AddFamilyMemberFormField.ssn]?.message}>
            <Controller
              name={AddFamilyMemberFormField.ssn}
              control={control}
              render={({ field }) => (
                <MaskTextInput
                  testId="add-family-member-from_ssn"
                  name={AddFamilyMemberFormField.ssn}
                  mask={masks.SSN}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('Enter SSN')}
                  error={!!errors?.[AddFamilyMemberFormField.ssn]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label
            name={AddFamilyMemberFormField.relationship}
            isNarrow
            showRequireIcon
          >
            {t('Relationship')}
          </Label>
          <ControllerWrapper>
            <Controller
              name={AddFamilyMemberFormField.relationship}
              control={control}
              render={({ field }) => (
                <AutocompleteDropdown
                  testId="add-family-member-from_relationship"
                  name={AddFamilyMemberFormField.relationship}
                  value={field.value}
                  onChange={field.onChange}
                  options={RELATIONSHIP_OPTIONS}
                  defaultValue={field.value}
                  placeholder={t('Select relationship')}
                  errorText={errors?.[AddFamilyMemberFormField.relationship]?.message}
                />
              )}
            />
          </ControllerWrapper>
        </FieldWrapper>
      </FormFieldsContainer>
    </Box>
  );
};

export default AddFamilyMemberModalForm;
